import { Button, formatDateTime, PageEmpty, PageError, PageHeader, PageLoading, TextButton } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { Select } from "@majitap/ui/arc/select/select";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import {
  type Link as AccountingLink,
  type Attention,
  accountingLinksQuery,
  connectionActions,
  ENTITY,
  type Integration,
  integrationsQuery,
  itemMappings,
  productItemsQuery,
  quickbooksConnected,
  quickbooksListQuery,
  registeredTaxItemsQuery,
  SYNC_STATUS,
  syncable,
  TAX_TYPE,
  type TaxCode,
  type TaxLink,
  taxLinksQuery,
} from "../../connections";
import { messageFor } from "../../error-copy";
import { filterSearch } from "../../list-search";
import { RouterLink } from "../../router-link";
import { targetHref } from "../../team";
import styles from "./settings.module.css";

export const accountingFilters = filterSearch({ book: ["quickbooks", "etims"] as const, integration: "text" });

type Item = { id: string; name: string; sku: string; product: string };
type Row<L> = { id: string; item: Item; link: L | null };

/** Accounting & tax (P8-23): product items mapped to QuickBooks and eTIMS items, and what failed to sync. */
export function AccountingPage(): ReactNode {
  const search = useSearch({ from: "/console/settings/accounting" });
  const navigate = useNavigate({ from: "/settings/accounting" });
  const integrations = useQuery(integrationsQuery);
  const book = search.book ?? "quickbooks";
  const provider = book === "quickbooks" ? "quickbooks" : "digitax";
  const choices = (integrations.data ?? []).filter((i) => i.provider === provider);
  const chosen = choices.find((i) => i.id === search.integration) ?? choices[0];
  return (
    <>
      <PageHeader
        title="Accounting & tax"
        description="Which QuickBooks and eTIMS item each product item is, and what didn't sync."
      />
      <div className={styles.toolbar}>
        <SegmentedControl
          label="Show"
          value={book}
          onValueChange={(v) => navigate({ search: { book: v === "quickbooks" ? undefined : "etims" } })}
          options={[
            { value: "quickbooks", label: "QuickBooks" },
            { value: "etims", label: "KRA eTIMS" },
          ]}
        />
        {choices.length > 1 && chosen ? (
          <Select
            label="Integration"
            value={chosen.id}
            onValueChange={(v) => navigate({ search: (s) => ({ ...s, integration: v }) })}
            options={choices.map((i) => ({ value: i.id, label: i.name }))}
          />
        ) : null}
      </div>
      {integrations.status === "pending" ? (
        <PageLoading label="Loading" />
      ) : integrations.status === "error" ? (
        <PageError message={messageFor(errorOf(integrations.error))} onRetry={() => integrations.refetch()} />
      ) : !chosen ? (
        <PageEmpty
          title={book === "quickbooks" ? "QuickBooks isn't set up" : "eTIMS isn't set up"}
          description={`Add ${book === "quickbooks" ? "QuickBooks Online" : "KRA eTIMS (DigiTax)"} in Integrations first.`}
        />
      ) : book === "quickbooks" ? (
        <QuickbooksView integration={chosen} />
      ) : (
        <TaxView integration={chosen} />
      )}
    </>
  );
}

function NotOn({ integration: i, extra }: { integration: Integration; extra?: string }) {
  return (
    <Alert tone="warning" title={`${i.name} is off`}>
      Nothing syncs while it's off.{extra ? ` ${extra}` : ""} Turn it on from{" "}
      <Link to="/settings/integrations/$integrationId" params={{ integrationId: i.id }} className={styles.link}>
        its page
      </Link>
      .
    </Alert>
  );
}

function QuickbooksView({ integration: i }: { integration: Integration }) {
  const items = useQuery(productItemsQuery);
  const links = useQuery(accountingLinksQuery(i.id, { entity_type: "product_item" }));
  const [attention, setAttention] = useState<Attention>("failed");
  const failed = useQuery(accountingLinksQuery(i.id, { sync_status: attention }));
  const [mapping, setMapping] = useState<Row<AccountingLink> | null>(null);
  return (
    <div className={styles.panel}>
      {i.status !== "active" ? <NotOn integration={i} /> : null}
      {i.status === "active" && !quickbooksConnected(i) ? (
        <Alert tone="warning" title="QuickBooks isn't connected">
          Connect it from{" "}
          <Link to="/settings/integrations/$integrationId" params={{ integrationId: i.id }} className={styles.link}>
            its page
          </Link>{" "}
          before anything syncs.
        </Alert>
      ) : null}
      {items.status === "error" || links.status === "error" ? (
        <PageError message={messageFor(errorOf(items.error ?? links.error))} onRetry={() => void links.refetch()} />
      ) : !items.data || !links.data ? (
        <PageLoading label="Loading items" />
      ) : (
        <ItemMappingTable
          caption="QuickBooks items"
          externalLabel="QuickBooks item ID"
          rows={itemMappings(items.data, links.data)}
          actions={(row) => (
            <TextButton onClick={() => setMapping(row)}>{row.link?.external_id ? "Change" : "Map"}</TextButton>
          )}
        />
      )}
      <FailedLinks
        attention={attention}
        onAttention={setAttention}
        links={failed.data}
        error={failed.error}
        onRetry={() => void failed.refetch()}
        again={(l) => {
          const type = l.entity_type;
          return syncable(type) ? (
            <SyncAgain
              run={() => connectionActions.syncAgain(type, l.entity_id)}
              label={attention === "failed" ? "Sync again" : "Sync now"}
            />
          ) : null;
        }}
      />
      {mapping ? (
        <QuickbooksMapDialog
          integration={i}
          item={mapping.item}
          items={items.data ?? []}
          current={mapping.link?.external_id ?? ""}
          onClose={() => setMapping(null)}
        />
      ) : null}
    </div>
  );
}

function TaxView({ integration: i }: { integration: Integration }) {
  const items = useQuery(productItemsQuery);
  const links = useQuery(taxLinksQuery({ entity_type: "product_item" }));
  const [attention, setAttention] = useState<Attention>("failed");
  const failed = useQuery(taxLinksQuery({ sync_status: attention }));
  const [dialog, setDialog] = useState<{ row: Row<TaxLink>; mode: "map" | "register" } | null>(null);
  return (
    <div className={styles.panel}>
      {i.status !== "active" ? (
        <NotOn integration={i} extra="Invoices issued meanwhile aren't reported to KRA until it's back on." />
      ) : null}
      {items.status === "error" || links.status === "error" ? (
        <PageError message={messageFor(errorOf(items.error ?? links.error))} onRetry={() => void links.refetch()} />
      ) : !items.data || !links.data ? (
        <PageLoading label="Loading items" />
      ) : (
        <ItemMappingTable
          caption="eTIMS items"
          externalLabel="eTIMS item"
          rows={itemMappings(items.data, links.data)}
          actions={(row) =>
            row.link?.external_id ? (
              <TextButton onClick={() => setDialog({ row, mode: "map" })}>Change</TextButton>
            ) : (
              <span className={styles.actions}>
                <TextButton onClick={() => setDialog({ row, mode: "register" })}>Register</TextButton>
                <TextButton onClick={() => setDialog({ row, mode: "map" })}>Map existing</TextButton>
              </span>
            )
          }
        />
      )}
      <FailedLinks
        attention={attention}
        onAttention={setAttention}
        links={failed.data}
        error={failed.error}
        onRetry={() => void failed.refetch()}
        again={(l) =>
          l.entity_type === "invoice" || l.entity_type === "invoice_credit_note" ? (
            <SyncAgain
              run={() =>
                connectionActions.resubmitTax(l.entity_id, l.entity_type === "invoice" ? "sale" : "credit_note")
              }
              label="Send again"
            />
          ) : null
        }
      />
      {dialog?.mode === "map" ? (
        <TaxMapDialog
          integrationId={i.id}
          item={dialog.row.item}
          items={items.data ?? []}
          current={dialog.row.link?.external_id ?? ""}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.mode === "register" ? (
        <RegisterDialog integrationId={i.id} item={dialog.row.item} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}

/** Each product item beside its item in the other system, and whether it synced. */
export function ItemMappingTable<
  L extends { external_id: string | null; sync_status: keyof typeof SYNC_STATUS; last_error: string | null },
>({
  caption,
  externalLabel,
  rows,
  actions,
}: {
  caption: string;
  externalLabel: string;
  rows: Row<L>[];
  actions: (row: Row<L>) => ReactNode;
}) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Product items</h2>
      <SortableDataTable
        caption={caption}
        rowKey="id"
        rows={rows}
        itemName={{ one: "item", other: "items" }}
        columns={[
          {
            key: "item",
            label: "Item",
            sortable: false,
            render: (_v, r) => (
              <span className={styles.stack}>
                <span>
                  {r.item.product} · {r.item.name}
                </span>
                <span className={styles.muted}>{r.item.sku}</span>
              </span>
            ),
          },
          {
            key: "link",
            label: externalLabel,
            sortable: false,
            render: (_v, r) =>
              r.link?.external_id ? (
                <span className={styles.code}>{r.link.external_id}</span>
              ) : (
                <span className={styles.muted}>Not mapped</span>
              ),
          },
          {
            key: "status",
            label: "Sync",
            sortable: false,
            render: (_v, r) => {
              if (!r.link) return <span className={styles.muted}>—</span>;
              const [label, tone] = SYNC_STATUS[r.link.sync_status];
              return (
                <span className={styles.stack}>
                  <Badge tone={tone}>{label}</Badge>
                  {r.link.last_error ? <span className={styles.muted}>{r.link.last_error}</span> : null}
                </span>
              );
            },
          },
          { key: "actions", label: "", sortable: false, render: (_v, r) => actions(r) },
        ]}
      />
    </section>
  );
}

/** What didn't sync, with the provider's reason and a way to try again. */
function FailedLinks<
  L extends { id: string; entity_type: string; entity_id: string; last_error: string | null; updated_at: string },
>({
  attention,
  onAttention,
  links,
  error,
  onRetry,
  again,
}: {
  attention: Attention;
  onAttention: (a: Attention) => void;
  links: L[] | undefined;
  error: unknown;
  onRetry: () => void;
  again: (l: L) => ReactNode;
}) {
  const format = useCompanyFormat();
  const empty = {
    failed: "Nothing failed. Everything sent was accepted.",
    pending: "Nothing waiting: everything sent has an answer.",
    skipped: "Nothing set aside without sending.",
  }[attention];
  return (
    <section className={styles.section}>
      <div className={styles.head}>
        <h2 className={styles.sectionTitle}>Needs a look</h2>
        <SegmentedControl
          label="Records"
          value={attention}
          onValueChange={(v) => onAttention(v as Attention)}
          options={[
            { value: "failed", label: "Didn't sync" },
            { value: "pending", label: "Waiting" },
            { value: "skipped", label: "Not sent" },
          ]}
        />
      </div>
      {error ? (
        <PageError message={messageFor(errorOf(error))} onRetry={onRetry} />
      ) : !links ? (
        <PageLoading label="Loading" />
      ) : links.length === 0 ? (
        <p className={styles.muted}>{empty}</p>
      ) : (
        <SortableDataTable
          caption="Needs a look"
          rowKey="id"
          rows={links}
          itemName={{ one: "record", other: "records" }}
          columns={[
            {
              key: "entity_type",
              label: "Record",
              sortable: false,
              render: (_v, l) => {
                const href = targetHref(
                  l.entity_type === "invoice_credit_note" ? "invoice" : l.entity_type,
                  l.entity_id,
                );
                const label = ENTITY[l.entity_type] ?? l.entity_type.replace(/_/g, " ");
                return href ? (
                  <RouterLink href={href} className={styles.link}>
                    {label}
                  </RouterLink>
                ) : (
                  label
                );
              },
            },
            {
              key: "last_error",
              label: "Why",
              sortable: false,
              render: (_v, l) =>
                l.last_error ? (
                  <span className={styles.danger}>{l.last_error}</span>
                ) : (
                  <span className={styles.muted}>—</span>
                ),
            },
            {
              key: "updated_at",
              label: "When",
              sortable: false,
              render: (_v, l) => <span className={styles.muted}>{formatDateTime(l.updated_at, format)}</span>,
            },
            { key: "actions", label: "", sortable: false, render: (_v, l) => again(l) },
          ]}
        />
      )}
    </section>
  );
}

/** Queues the record to go again; the result shows in this table when it's back. */
function SyncAgain({ run, label }: { run: () => Promise<unknown>; label: string }) {
  const queryClient = useQueryClient();
  const sync = useMutation({
    mutationFn: run,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["integrations"] }),
        queryClient.invalidateQueries({ queryKey: ["tax"] }),
      ]),
  });
  if (sync.isSuccess) return <span className={styles.muted}>Queued</span>;
  return (
    <span className={styles.stack}>
      <TextButton disabled={sync.isPending} onClick={() => sync.mutate()}>
        {label}
      </TextButton>
      {sync.error ? <span className={styles.danger}>{messageFor(errorOf(sync.error))}</span> : null}
    </span>
  );
}

/**
 * Maps a product item to an item already registered at DigiTax: picked from DigiTax's own list
 * (name, eTIMS code, KRA status), or its id typed in. DigiTax checks the id either way.
 */
function TaxMapDialog({
  integrationId,
  item,
  items,
  current,
  onClose,
}: {
  integrationId: string;
  item: Item;
  items: Item[];
  current: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const registered = useQuery(registeredTaxItemsQuery(integrationId));
  const [value, setValue] = useState(current);
  const save = useMutation({
    mutationFn: () => connectionActions.mapTaxItem(integrationId, item.id, value.trim()),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["integrations"] }),
        queryClient.invalidateQueries({ queryKey: ["tax"] }),
      ]);
      onClose();
    },
  });
  const nameOf = (id: string) => items.find((x) => x.id === id)?.name ?? "another item";
  const chosen = registered.data?.find((r) => r.id === value.trim());
  const elsewhere = chosen?.product_item_ids.filter((id) => id !== item.id) ?? [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title="Map to an eTIMS item" description={`${item.product} · ${item.name} (${item.sku})`}>
        <div className={styles.form}>
          {registered.status === "pending" ? (
            <PageLoading label="Asking DigiTax for its items" />
          ) : registered.status === "error" ? (
            <Alert tone="warning" title="DigiTax's items didn't load">
              {messageFor(errorOf(registered.error))} You can still type the item's ID below.
            </Alert>
          ) : registered.data.length === 0 ? (
            <p className={styles.muted}>Nothing is registered at DigiTax yet. Register this item instead.</p>
          ) : (
            <Select
              label="Registered at DigiTax"
              description="Items KRA refused can't be picked: register them again."
              placeholder="Pick an item"
              value={chosen ? chosen.id : ""}
              onValueChange={setValue}
              options={registered.data.map((r) => ({
                value: r.id,
                label: [
                  r.name,
                  r.item_code ?? "no eTIMS code yet",
                  REGISTRATION[r.status ?? ""] ?? r.status ?? "status unknown",
                ]
                  .filter(Boolean)
                  .join(" · "),
                disabled: r.status === "FAILED" || !r.active,
              }))}
            />
          )}
          <Input
            label="DigiTax item ID"
            description="Filled in when you pick an item. It starts with item_, not the eTIMS item code (KE…)."
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          {elsewhere.length ? (
            <Alert tone="info" title="Already mapped">
              {elsewhere.map(nameOf).join(", ")} {elsewhere.length === 1 ? "is" : "are"} mapped to this item too. That's
              fine when they're the same thing to KRA.
            </Alert>
          ) : null}
          {save.error ? (
            <Alert tone="danger" title="Not mapped">
              {messageFor(errorOf(save.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={!value.trim() || value.trim() === current}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              Map it
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Maps a product item to a product or service already in QuickBooks: picked from the company's
 * own list once it's connected, or its Id typed in.
 */
function QuickbooksMapDialog({
  integration: i,
  item,
  items,
  current,
  onClose,
}: {
  integration: Integration;
  item: Item;
  items: Item[];
  current: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const connected = quickbooksConnected(i);
  const listed = useQuery({ ...quickbooksListQuery(i.id, "items"), enabled: connected });
  const [value, setValue] = useState(current);
  const save = useMutation({
    mutationFn: () => connectionActions.mapQuickbooksItem(i.id, item.id, value.trim()),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["integrations"] }),
        queryClient.invalidateQueries({ queryKey: ["accounting"] }),
      ]);
      onClose();
    },
  });
  const chosen = listed.data?.find((o) => o.value === value.trim());
  const elsewhere = (chosen?.product_item_ids ?? []).filter((id) => id !== item.id);
  const nameOf = (id: string) => items.find((x) => x.id === id)?.name ?? "another item";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title="Map to a QuickBooks item" description={`${item.product} · ${item.name} (${item.sku})`}>
        <div className={styles.form}>
          {!connected ? (
            <p className={styles.muted}>Connect QuickBooks to pick from its products and services.</p>
          ) : listed.status === "pending" ? (
            <PageLoading label="Asking QuickBooks for its items" />
          ) : listed.status === "error" ? (
            <Alert tone="warning" title="QuickBooks's items didn't load">
              {messageFor(errorOf(listed.error))} You can still type the item's ID below.
            </Alert>
          ) : listed.data.length === 0 ? (
            <p className={styles.muted}>QuickBooks has no products or services yet.</p>
          ) : (
            <Select
              label="In QuickBooks"
              placeholder="Pick a product or service"
              value={chosen ? chosen.value : ""}
              onValueChange={setValue}
              options={listed.data.map((o) => ({ value: o.value, label: o.type ? `${o.label} · ${o.type}` : o.label }))}
            />
          )}
          <Input
            label="QuickBooks item ID"
            description="Filled in when you pick an item."
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          {elsewhere.length ? (
            <Alert tone="info" title="Already mapped">
              {elsewhere.map(nameOf).join(", ")} {elsewhere.length === 1 ? "is" : "are"} mapped to this item too.
            </Alert>
          ) : null}
          {save.error ? (
            <Alert tone="danger" title="Not mapped">
              {messageFor(errorOf(save.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={!value.trim() || value.trim() === current}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              Map it
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** KRA's answer to an item's registration, as DigiTax reports it. */
const REGISTRATION: Record<string, string> = {
  COMPLETE: "registered",
  COMPLETED: "registered",
  PENDING: "waiting for KRA",
  FAILED: "refused by KRA",
};

/** Registers the item with KRA through DigiTax, and maps it to what comes back. */
function RegisterDialog({ integrationId, item, onClose }: { integrationId: string; item: Item; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [classCode, setClassCode] = useState("");
  const [taxType, setTaxType] = useState<TaxCode>("B");
  const [itemType, setItemType] = useState<"1" | "2" | "3">("3");
  const register = useMutation({
    mutationFn: () =>
      connectionActions.registerTaxItem(integrationId, item.id, {
        class_code: classCode.trim(),
        tax_type_code: taxType,
        item_type_code: itemType,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["tax"] });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title="Register with KRA"
        description={`${item.product} · ${item.name} (${item.sku}). KRA keeps it: check the codes first.`}
      >
        <div className={styles.form}>
          <Input
            label="Item classification code"
            description="KRA's item class (itemClsCd), e.g. 99011095"
            value={classCode}
            onChange={(e) => setClassCode(e.target.value)}
          />
          <div className={styles.pair}>
            <Select
              label="Tax type"
              value={taxType}
              onValueChange={(v) => setTaxType(v as TaxCode)}
              options={Object.entries(TAX_TYPE).map(([value, label]) => ({ value, label }))}
            />
            <Select
              label="Item type"
              value={itemType}
              onValueChange={(v) => setItemType(v as "1" | "2" | "3")}
              options={[
                { value: "3", label: "Service" },
                { value: "2", label: "Finished goods" },
                { value: "1", label: "Raw material" },
              ]}
            />
          </div>
          {register.error ? (
            <Alert tone="danger" title="Not registered">
              {messageFor(errorOf(register.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!classCode.trim()} loading={register.isPending} onClick={() => register.mutate()}>
              Register
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
